"use client";

import { memo } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Monitor, Send, Webhook, MessageSquare } from "lucide-react";
import type { Target, TargetKind } from "@/lib/types/routing";

const KIND_LABELS: Record<TargetKind, string> = {
  Mt5: "MT5",
  TelegramChannel: "Telegram",
  DiscordWebhook: "Discord",
  HttpWebhook: "HTTP",
};

const KIND_ICONS: Record<TargetKind, typeof Monitor> = {
  Mt5: Monitor,
  TelegramChannel: Send,
  DiscordWebhook: MessageSquare,
  HttpWebhook: Webhook,
};

export interface TargetNodeData {
  target: Target;
  onNodeClick?: (target: Target) => void;
}

function TargetNodeComponent({ data }: NodeProps) {
  const nodeData = data as unknown as TargetNodeData;
  const { target, onNodeClick } = nodeData;
  const Icon = KIND_ICONS[target.kind] ?? Monitor;
  const label = KIND_LABELS[target.kind] ?? target.kind;

  const isMt5 = target.kind === "Mt5";
  const isOnline = isMt5 && target.status === "online";

  return (
    <div
      className="group flex min-w-[180px] cursor-pointer flex-col border border-border bg-background px-3 py-2.5 shadow-sm transition-colors hover:border-primary/60 hover:bg-muted/30"
      onClick={() => onNodeClick?.(target)}
    >
      {/* left-side input handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="in"
        style={{
          width: 10,
          height: 10,
          background: "hsl(var(--primary))",
          border: "2px solid hsl(var(--background))",
          borderRadius: 0,
          left: -6,
        }}
      />

      {/* kind badge row */}
      <div className="mb-1.5 flex items-center gap-1.5">
        <Icon className="h-3 w-3 text-muted-foreground" />
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
          {label}
        </span>
        {isMt5 && (
          <span
            className={
              "ms-auto h-1.5 w-1.5 rounded-full " +
              (isOnline ? "bg-profit" : "bg-muted-foreground/40")
            }
            title={isOnline ? "Online" : "Offline"}
          />
        )}
        {!isMt5 && (
          <span
            className={
              "ms-auto h-1.5 w-1.5 rounded-full " +
              (target.isActive ? "bg-profit" : "bg-loss")
            }
          />
        )}
      </div>
      {/* name */}
      <p className="truncate text-sm font-medium leading-tight">{target.name}</p>
    </div>
  );
}

export const TargetNode = memo(TargetNodeComponent);
